import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authApi, ApiError } from '../api/client';
import { resolvePostAuthPath } from '../utils/postAuthNavigation';

/**
 * Consumes a short-lived Studio → Pages handoff code (`?code=`),
 * stores the session, and redirects into the app.
 */
export const StudioHandoff: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const code = searchParams.get('code')?.trim();
    if (!code) {
      setError('Missing sign-in code. Open Compete Pages from Compete Studio again.');
      return;
    }

    void (async () => {
      try {
        const response = await authApi.exchangeStudioHandoff(code);
        const u = response.user as { emailVerified?: boolean; isAdmin?: boolean };
        if (!u.isAdmin && u.emailVerified === false) {
          navigate('/verify-email', { replace: true });
          return;
        }
        const destination = await resolvePostAuthPath(u);
        navigate(destination, { replace: true });
      } catch (err) {
        setError(
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Sign-in link expired. Open Compete Pages from Compete Studio again.'
        );
      }
    })();
  }, [searchParams, navigate]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-4">
        <p className="max-w-md text-center text-sm text-red-600">{error}</p>
        <Link
          to="/login"
          className="text-sm font-medium text-blue-600 underline hover:text-blue-800"
        >
          Go to login
        </Link>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3">
      <div
        className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600"
        aria-hidden
      />
      <p className="text-sm text-gray-600">Signing you in to Compete Pages…</p>
    </div>
  );
};
